import { getAccessToken } from "./authService";
import { FileIndex, UserSession } from "../types";

export const listFiles = async () => {
  const result = await fetch("/api/files", {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getAccessToken()}`,
    },
  });
  const data = await result.json();
  return data.files as FileIndex[];
};

export const getFileUrl = async (fileName: string) => {
  const result = await fetch(`/api/files/${fileName}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getAccessToken()}`,
    },
  });
  return await result.text();
};
export const getFile = async (fileName: string) => {
  const signedUrl = await getFileUrl(fileName);
  const fileRequest = await fetch(signedUrl, {
    mode: "cors",
  });
  return await fileRequest.text();
};

export const getUploadUrl = async (file: File) => {
  const result = await fetch("/api/files", {
    method: "POST",
    body: JSON.stringify({ fileName: file.name, isDirectory: false }),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getAccessToken()}`,
    },
  });
  return await result.text();
};

export const createNewFolder = async (folderName: string) => {
  const result = await fetch("/api/files", {
    method: "POST",
    body: JSON.stringify({ fileName: folderName, isDirectory: true }),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getAccessToken()}`,
    },
  });
  return await result.text();
};

export const uploadFile = async (
  file: File,
  uploadUrl: string,
  progressCallback: (progress: number) => void
) => {
  const totalSize = file.size;
  let loaded = 0;
  const progressStream = new TransformStream({
    transform(chunk, controller) {
      loaded += chunk.byteLength;
      const progress = (loaded / totalSize) * 100;
      progressCallback(progress);
      controller.enqueue(chunk);
    },
  });
  const uploadResult = await fetch(
    new Request(uploadUrl, {
      method: "PUT",
      body: file.stream().pipeThrough(progressStream),
      headers: new Headers({ "Content-Type": "multipart/*" }),
    })
  );
  if (uploadResult.ok) {
    return `Successly uploaded ${file.name}`;
  } else {
    return await uploadResult.text();
  }
};

export const renameFile = async (oldName: string, newName: string) => {
  const result = await fetch(`/api/files/${oldName}`, {
    method: "PUT",
    body: JSON.stringify({ newFileName: newName }),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getAccessToken()}`,
    },
  });
  return await result.text();
};

export const assignToFolder = async (item: FileIndex, folder: FileIndex) => {
  const result = await fetch(`/api/files/assign-parent`, {
    method: "PUT",
    body: JSON.stringify({
      fileName: item.fileName,
      parentId: folder.fileName,
    }),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getAccessToken()}`,
    },
  });
  return result.ok;
};

export const deleteFile = async (name: string) => {
  const result = await fetch(`/api/files/${name}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getAccessToken()}`,
    },
  });
  return await result.text();
};

export const getUserSession = async () => {
  const result = await fetch(`/api/files/session`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getAccessToken()}`,
    },
  });
  if (result.ok) {
    const data = await result.json();
    return {
      autoplay: data.autoplay,
      playedIndices: data.playedIndices,
      searchText: data.searchText,
      selectedFileNames: data.selectedFileNames ?? [],
      shuffle: data.shuffle,
      typeFilter: data.typeFilter,
      playedSeconds: Number(data.playedSeconds),
    } as UserSession;
  }
  return {
    autoplay: false,
    playedIndices: [],
    searchText: "",
    selectedFileIndex: -1,
    shuffle: false,
    typeFilter: "all",
    playedSeconds: 0,
    selectedFileNames: [],
  } as UserSession;
};

export const saveUserSession = async (data: UserSession) => {
  const result = await fetch(`/api/files/session`, {
    method: "POST",
    body: JSON.stringify({
      selectedFileNames: data.selectedFileNames,
      searchText: data.searchText,
      typeFilter: data.typeFilter,
      autoplay: data.autoplay,
      shuffle: data.shuffle,
      playedIndices: data.playedIndices,
      playedSeconds: data.playedSeconds.toString(),
    }),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getAccessToken()}`,
    },
  });
  return (await result.json()) as UserSession;
};

// eslint-disable-next-line
export default {
  listFiles,
  getFileUrl,
  getFile,
  getUploadUrl,
  uploadFile,
  createNewFolder,
  renameFile,
  assignToFolder,
  deleteFile,
  saveUserSession,
  getUserSession,
};
